import { Loader2 } from 'lucide-react';
import { useCategories } from './catalogCategories';
import { touchRows, useHardwareInteractions } from './hardwareInteractionsRun';
import { useModelSizes, type ByPart } from './partModelSize';
import { useInsertion } from './insertionRun';
import { usePartsRun } from './partsRun';
import { INNER_CELL, TableCard } from './TableCard';
import { byPiece, HolesGrid, holeRows, twiceIdsOf, type HoleRow } from './HolesGrid';
import { useLanguage } from '../../contexts/LanguageContext';

// WHAT TO DRILL, ONE ROW PER PIECE OF HARDWARE'S LINE — read off the two tables before it and
// nothing else. The Joints table says which holes there are (ids, parts, faces, kinds, the hardware
// each is for, and which other part's hole the same hardware runs on into); the Insertion table how
// wide the hardware's hole is and how much of it is inside the parts.
//
// ONE ROW PER LINE, laid out as the Joints table's (see HolesGrid), with two figures after.
//
// THE DEPTH IS THE INSERTION TABLE'S, as answered: how much of the hardware is inside the parts —
// over every hole of the row together. Not split hole by hole: the first hole of a line goes through
// its part, and how thick that part is where the hole is drilled is only known once the hole is
// placed. No fallback: hardware it has not answered says so.
//
// WHAT IT CANNOT SAY: where on the face (Find holes' answer, against the 3D models), a clearance
// hole wider than the pilot, a counterbore under a sunk head.

// EACH PIECE'S TWO FIGURES, a line each as the Hardware cell has them — the Insertion table's diameter
// and inside length; the hardware it has not answered said so, in the Diameter cell, and no depth.
// The Drilling table's last two columns, and the found holes' table's (see ExpectedLines).
export const drillingCells = (r: HoleRow, sizes: ByPart, t: (k: string) => string) => (
  <>
    <td className={INNER_CELL}>
      {byPiece(r, (hw) => {
        const answer = sizes[hw]?.insertion;
        return answer
          ? answer.holeDiameterMm ?? ''
          : <span className="text-amber-600">{t('notice.hardwareAxis.unanswered')}</span>;
      })}
    </td>
    <td className={INNER_CELL}>{byPiece(r, (hw) => sizes[hw]?.insertion?.insideMm ?? '')}</td>
  </>
);

export function DrillingTable({ docId }: { docId: string }) {
  const { t } = useLanguage();
  const { items } = usePartsRun(docId);
  const sizes = useModelSizes(docId).byPart;
  const found = useHardwareInteractions(docId);
  // WHILE EITHER TABLE IT IS READ OFF IS ASKED AGAIN, A SPINNER: the rows on screen would be the
  // last answers, about to be replaced.
  const insertion = useInsertion(docId);
  const busy = found.busy || insertion.busy;
  // Subscribed so the names follow the ontology once it lands.
  useCategories();

  // EVERY LINE OF EVERY ROW of the Joints table, in one table.
  const entries = touchRows(found);
  const rows = holeRows(entries);
  if (!rows.length && !busy) return null;
  const twiceIds = twiceIdsOf(entries.flatMap((e) => e.holes));

  return (
    <TableCard title={t('notice.drilling.title')}>
      {busy ? (
        <Loader2 className="mx-auto my-6 h-6 w-6 animate-spin text-slate-400" />
      ) : (
        <div className="flex justify-center text-xs text-slate-700">
          <HolesGrid
            rows={rows}
            items={items}
            twiceIds={twiceIds}
            extra={{
              heads: [t('notice.drilling.diameter'), t('notice.drilling.depth')],
              cells: (r) => drillingCells(r, sizes, t),
            }}
          />
        </div>
      )}
    </TableCard>
  );
}
